import { createContext, FC, useCallback, useContext, useState } from "react";
import { User } from "./user.ts";

type AuthContextValues = {
  token: string | null;
  isAuthenticated: boolean;
  setUser: (user: User) => void;
  clearToken: () => void;
  user: User | null;
};

const noop = () => {};

const AuthContext = createContext<AuthContextValues>({
  user: null,
  setUser: noop,
  token: null,
  isAuthenticated: false,
  clearToken: noop,
});

type AuthProviderProps = {
  children: React.ReactNode;
};

const getToken = (): string | null => {
  const token = localStorage.getItem("token");
  return token;
};

const getUser = (): User | null => {
  const user = localStorage.getItem("user");

  if (typeof user === 'string'){
    return JSON.parse(user) as User;
  }

  return user;
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [token, updateToken] = useState<string | null>(getToken());
  const [user, setUser] = useState<User | null>(getUser());

  const clearToken = useCallback(() => {
    updateToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem('user');
  }, []);

  const updatedUser = useCallback((user: User) => {
    updateToken(user.jwtToken);
    setUser(user);

    localStorage.setItem("token", user.jwtToken);
    localStorage.setItem('user', JSON.stringify(user));
  }, [])


  return (
    <AuthContext.Provider
      value={{
        user,
        setUser: updatedUser,
        token,
        isAuthenticated: Boolean(token),
        clearToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("useAuth was used outside of its Provider");
  }

  return authContext;
};
